<template>
  <div class="invoice">
    <Spinner :loading="invoice.loading" />
    <div class="inv-inner" v-if="!invoice.loading">
      <!-- Invoice Header -->
      <div class="inv-header">
        <Icon
          class="h-6 w-auto"
          name="logo"
          v-if="invoice.data.data.order.status == 'draft'"
        />
        <Icon class="h-6 w-auto" name="logo-invoice" v-else />
        <div class="inv-customer-name">
          {{ invoice.data.data.company.name }}
        </div>
        <div>
          <div class="inv-number">
            <span>{{ invoice.data.data.order.status == "draft" ? "" : "Invoice #" }}</span>
            <p v-if="invoice.data.data.order.status != 'draft'">{{ invoice.data.data.invoice_number }}</p>
          </div>
          <div class="inv-number" v-if="invoice.data.data.order.reference_number">
            <span>Reference #</span>
            <p>{{ invoice.data.data.order.reference_number }}</p>
          </div>
        </div>
      </div>
      <div class="inv-icon" :class="invoice.data.data.packingList ? 'cursor-pointer' : ''">
        <span @click="packingList(invoice)"><Icon name="package-filled"/></span>
      </div>
      <div class="badges" v-if="invoice.data.data.order.status != 'draft'">
        <div class="invoice-status" :class="invoice.data.data.payment_status">
          <span>{{ invoice.data.data.payment_status }}</span>
        </div>
      </div>

      <!-- Invoice Content -->
      <div class="inv-details">
        <div class="address">
          <div class="address-grid">
            <div>
              <span class="small">Bill to</span>
              <p>
                {{ 
                  invoice.data.data.billing_address.nickname
                    ? invoice.data.data.billing_address.nickname
                    : invoice.data.data.company.name 
                }}
              </p>
              <span>
                <div>{{ invoice.data.data.billing_address.address1 }}</div>
                <div>{{ invoice.data.data.billing_address.address2 }}</div>
                <div>
                  {{ invoice.data.data.billing_address.city }}, {{ invoice.data.data.billing_address.state }}
                  {{ invoice.data.data.billing_address.zip_code }}
                </div>
              </span>
            </div>
            <div>
              <span class="small">Ship to</span>
              <p>
                {{
                  invoice.data.data.shipping_address.nickname
                    ? invoice.data.data.shipping_address.nickname
                    : invoice.data.data.company.name
                }}
              </p>
              <span>
                <div>{{ invoice.data.data.shipping_address.address1 }}</div>
                <div>{{ invoice.data.data.shipping_address.address2 }}</div>
                <div>
                  {{ invoice.data.data.shipping_address.city }},
                  {{ invoice.data.data.shipping_address.state }}
                  {{ invoice.data.data.shipping_address.zip_code }}
                </div>
              </span>
            </div>
          </div>
          <div v-if="invoice.data.data.company.assignee">
            <span class="small">Representative</span>
            <p>
              {{ invoice.data.data.company.assignee.name + " " + invoice.data.data.company.assignee.last_name }}
            </p>
            <span>
              <div>{{ invoice.data.data.company.assignee.email }}</div>
              <div>{{ invoice.data.data.company.assignee.phone_number }}</div>
            </span>
          </div>
        </div>
        <div class="address mt-7">
          <div>
            <div class="flex gap-7 justify-between">
              {{
                invoice.data.data.order.status != "draft" ? "Order Number" : "Quote Number"
              }}
              <p>{{ invoice.data.data.order.order_number }}</p>
            </div>
            <div class="flex gap-7 justify-between">
              {{ invoice.data.data.order.status != "draft" ? "Order Date" : "Quote Date" }}
              <p>{{ moment(invoice.data.data.order.created_at) }}</p>
            </div>
            <div class="flex gap-7 justify-between" v-if="invoice.data.data?.lastPaymentDate">
              Last Payment Date
              <p>{{ moment(invoice.data.data.lastPaymentDate) }}</p>
            </div>
            <div
              class="flex gap-7 justify-between"
              v-if="invoice.data.data.order.status != 'draft' && invoice.data.data.payment_status != 'paid'"
            >
              Payment Due
              <p>{{ moment(invoice.data.data.due_at) }}</p>
            </div>
          </div>
          <div class="pt-12 flex items-start justify-end hide-on-print">
            <button
              :disabled="loader"
              :class="[
                'inline-flex items-center space-x-1 px-4 py-1.5 rounded leading-none text-white',
                loader ? 'bg-gray-500 cursor-not-allowed' : 'bg-theme-secondary'
              ]"
              @click="downloadInvoice(invoice.data.data)"
            >
              <Spinner :loading="loader" className="w-3.5 h-3.5" v-if="loader" />
              <DownloadIcon class="w-3.5 h-3.5" v-else />
              <span>Download</span>
            </button>
          </div>
        </div>
      </div>

      <!-- List Items -->
      <div class="inv-list">
        <div class="inv-list-header">
          <div class="name">Item</div>
          <div class="price">
            {{ adminInvoice ? "Customer Price" : "Price" }}
          </div>
          <div class="price" v-if="adminInvoice">GGV Price</div>
          <div class="qty">Qty</div>
          <div class="total">Total</div>
        </div>
        <div class="inv-list-items">
          <template v-for="item in invoice.data.data.invoice_line_items" :key="item.id">
            <div v-if="item.line_type == 'product'" class="inv-list-item">
              <div class="name">
                <img :src="item.image_url" :alt="item.variant_name" />
                <div class="item-info">
                  <p>
                    {{ item.product_name }} - {{ item.variant_name }}
                    <spna v-if="item.is_bundle == 1">(Bundle)</spna>
                  </p>
                  <span class="flex items-center gap-2">
                    {{ item.sku }}
                    <span v-if="item.discount > 0 || item.coupon_discount > 0" class="was-price">
                      WAS {{ Number(item.price).toFixed(2) }}
                      <span v-if="item.coupon_discount > 0">({{ getCouponLabel() }})</span>
                      <span v-else-if="item.discount">({{ item.discount }}% Line Discount)</span>
                    </span>
                  </span>
                </div>
              </div>
              <!-- <div class="price">{{ Number(item.price).toFixed(2) }}</div> -->
              <div class="price">{{ Number(getPrice(item)).toFixed(2) }}</div>
              <div class="price" v-if="adminInvoice">
                {{ Number(getGGVPrice(item)).toFixed(2) }}
              </div>
              <div class="qty">{{ parseInt(item.quantity) }}</div>
              <div class="total" v-if="adminInvoice">
                {{ currencyFormat(item.quantity * getGGVPrice(item)) }}
              </div>
              <div class="total" v-else>
                {{ currencyFormat(item.quantity * getPrice(item)) }}
              </div>
            </div>
          </template>
        </div>
        <div class="inv-pricing">
          <div class="tax-pricings">
            <div
              class="flex items-center space-x-2 mb-3"
              v-if="invoice.data.data.order.totalTax"
            >
              <Icon class="w-5 h-5 -mt-0.5" name="document-filled" />
              <span class="text-md font-medium">Tax Summary</span>
            </div>
            <div
              class="price-item"
              v-for="(tax, label) in invoice.data.data.order.variantTaxLabels"
              :key="label"
            >
              <p>{{ label }}</p>
              <span>${{ commaFormat(Number(tax).toFixed(2)) }}</span>
            </div>
            <div class="price-item highlight" v-if="invoice.data.data.order.totalTax">
              <p>Total Taxes</p>
              <span
                >${{ commaFormat(Number(invoice.data.data.order.totalTax).toFixed(2)) }}</span
              >
            </div>
          </div>
          <div class="order-pricings">
            <div class="price-item">
              <p>Subtotal</p>
              <span
                >${{ commaFormat(Number(getSubTotal(invoice)).toFixed(2)) }}</span
              >
            </div>
            <div v-if="invoice.data.data.order.totalTax" class="price-item">
              <p>Taxes</p>
              <span
                >${{ commaFormat(Number(invoice.data.data.order.totalTax).toFixed(2)) }}</span
              >
            </div>
            <div
              v-if="
                invoice.data.data.invoice_line_items.filter(
                  (r) => r.line_type == 'shipping'
                ) && invoice.data.data.order.status != 'draft'
              "
              class="price-item"
            >
              <p>Shipping</p>
              <span
                >${{
                  Number(
                    invoice.data.data.invoice_line_items.filter(
                      (r) => r.line_type == "shipping"
                    )[0].price
                  ).toFixed(2)
                }}</span
              >
            </div>
            <!-- <div class="price-item highlight">
              <p>Sub Total</p>
              <span v-if="invoice.data.data.order.status != 'draft'"
                >${{
                  commaFormat(
                    Number(
                      parseFloat(
                        invoice.data.data.invoice_line_items.filter(
                          (r) => r.line_type == "shipping"
                        )[0].price
                      ) +
                        parseFloat(invoice.data.data.order.totalTax) +
                        parseFloat(getSubTotal(invoice))
                    ).toFixed(2)
                  )
                }}</span
              >
              <span v-else
                >${{
                  commaFormat(
                    Number(
                      parseFloat(invoice.data.data.order.totalTax) +
                        parseFloat(getSubTotal(invoice))
                    ).toFixed(2)
                  )
                }}</span
              >
            </div> -->
            <template v-for="item in invoice.data.data.invoice_line_items" :key="item.id">
              <div v-if="item.line_type == 'discount' && showDiscount(item)" class="price-item">
                <p>{{ item.label }}</p>
                <span
                  >-${{
                    commaFormat(Number(Math.abs(item.price)).toFixed(2))
                  }}</span
                >
              </div>
            </template>
            <div class="price-item highlight pb-zero">
              <p>Order Total</p>
              <span
                >${{ commaFormat(Number(getOrderTotal(invoice)).toFixed(2)) }}</span
              >
            </div>
            <div
              class="price-item highlight-noborder"
              v-if="invoice.data.data.order.status != 'draft'"
            >
              <p>Paid</p>
              <span>${{ commaFormat(Number(invoice.data.data.PaidAmount ?? 0).toFixed(2)) }}</span>
            </div>
            <div
              class="price-item highlight-noborder"
              v-if="invoice.data.data.order.status != 'draft'"
            >
              <p>Balance</p>
              <span
                >${{
                  commaFormat(
                    Number(
                      parseFloat(getOrderTotal(invoice)) -
                        parseFloat(invoice.data.data.PaidAmount ?? 0)
                    ).toFixed(2)
                  )
                }}</span
              >
            </div>
          </div>
        </div>
      </div>

      <div class="p-5 text-left space-y-3 border-2 border-gray-200 m-4">
        <p
          class="text-theme-secondary font-light text-xs"
          v-if="invoice.data.data.invoiceText && invoice.data.data.order.totalTax"
        >
          {{ getContent(invoice, invoice.data.data.invoiceText.content) }}
        </p>
        <p
          class="text-theme-secondary font-light text-xs"
          v-if="invoice.data.data.invoiceText && invoice.data.data.order.totalTax"
        >
          {{ getContent(invoice, invoice.data.data.invoiceText.open_system, "open") }}
        </p>
        <p
          class="text-theme-secondary font-light text-xs"
          v-if="invoice.data.data.invoiceText && invoice.data.data.order.totalTax"
        >
          {{ getContent(invoice, invoice.data.data.invoiceText.close_system, "closed") }}
        </p>
        <p
          class="text-theme-secondary font-light text-xs"
          v-if="invoice.data.data.invoiceText && invoice.data.data.order.totalTax"
        >
          {{ getContent(invoice, invoice.data.data.invoiceText.text, "text") }}
        </p>
        <p
          class="text-theme-secondary font-medium text-xs"
          v-if="invoice.data.data.invoiceText"
        >
          {{ invoice.data.data.invoiceText.license?.medusa_license_number }}
        </p>
        <p
          class="text-theme-secondary font-medium text-xs"
          v-if="invoice.data.data.shipping_address.state_code == 'VA' && invoice.data.data.order.totalTax == 0 && !invoice.data.data.company.tags.includes('COLLECT')"
        >
          Virginia Tobacco Products Tax NOT Paid
        </p>
        <p
          class="text-theme-secondary font-medium text-xs"
          v-if="invoice.data.data.shipping_address.state_code == 'VA' && invoice.data.data.order.TotalTax == 0 && !invoice.data.data.company.tags.includes('COLLECT')"
        >
          Virginia Tobacco Products Tax NOT Paid
        </p>
        <p class="text-theme-dark-shade font-medium text-xs pt-2">
          <span v-if="invoice.data.data.order.status !== 'draft'">
            We appreciate your business.
          </span>
          <span v-else>
            Please note: this is a DRAFT ORDER and the inventory will not be reserved for you until you ask us to finalize this order. Thank you.
          </span>

          <span class="block mt-1 italic">
            Products are sold subject to applicable laws. You are solely responsible for determining and complying with all federal, state, and local regulations where you operate. Our shipment to your designated address does not represent or warrant that resale or use is lawful in that jurisdiction.
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener"
              class="underline text-theme-primary"
            >
              Terms &amp; Conditions
            </a>.
          </span>
        </p>
      </div>
      <!-- <div class="inv-qr-code px-10 py-14">
        <div class="flex items-start">
          <img src="@/assets/images/qrcode.png" alt="" class="w-24 h-24" />
          <div class="flex flex-col text-sm p-2 font-light max-w-xs">
            <p class="text-theme-secondary font-normal">Terms</p>
            <p class="text-theme-body">
              Your prompt payment is greatly appreciated.
            </p>
          </div>
        </div>
      </div> -->

      <!-- Invoice Footer -->
      <div class="inv-footer">
        <span
          >Medusa Distribution LLC 9 Woodland Rd., Suite D,<br />
          Roseland, NJ, 07068, US
        </span>
        <div class="info">
          <span>Email sales@medusadistribution.com</span>
          <span>Phone 973-808-1101</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Mymixin from "@/modules/mixins";
import Account from "@/modules/Account";
import { DownloadIcon } from "@heroicons/vue/outline";
import { useRoute } from "vue-router";
import { ref, onMounted, inject } from "vue";

export default {
  name: "Invoice",
  mixins: [Mymixin],
  components: {
    DownloadIcon,
  },
  methods: {
    moment: function(date) {
      return moment(date).format("MMM Do YYYY");
    },
    getOrderTotal(invoice) {
      let temp = 0;
      let discounts = 0;
      let shipping = 0;
      if (this.adminInvoice) {
        invoice.data.data.invoice_line_items.forEach(
          (element) => (temp += element.quantity * this.getGGVPrice(element))
        );

        // calculate discounts
        invoice.data.data.invoice_line_items.forEach((r) => {
          if (r.line_type == "discount") discounts += r.price;
        });

        // calculate shipping
        shipping =
          invoice.data.data.order.status != "draft"
            ? invoice.data.data.invoice_line_items.filter((r) => r.line_type == "shipping")[0]
                .price
            : 0;
        temp = temp + invoice.data.data.order.totalTax + shipping + discounts;
      } else {
        temp = invoice.data.data.order.orderTotal;
      }
      return temp;
    },
    getTotal(data) {
      let temp = 0;
      if (data)
        data.forEach(
          (element) =>
            (temp +=
              element.quantity * element.price +
              element.tax_rate_override / 100)
        );
      return temp;
    },
    getPrice(item) {
      if (item.discount > 0) {
        return item.price - item.price * (item.discount / 100);
      }
      if (item.coupon_discount > 0) {
        return item.price - item.coupon_discount;
      }
      return item.price;
    },
    showDiscount(item) {
      const isMedusaCoins = item.label?.includes("MEDUSA COINS");
      const isStoreCredit = item.label?.includes("StoreCredit");
      const hasCouponOnItems = this.invoice.data.data.invoice_line_items.some(
        (r) => r.coupon_discount > 0
      );
      return isMedusaCoins || isStoreCredit || !hasCouponOnItems;
    },
    getCouponLabel() {
      const couponLine = this.invoice.data.data.invoice_line_items.find(
        (r) =>
          r.line_type === "discount" &&
          !r.label?.includes("MEDUSA COINS") &&
          !r.label?.includes("StoreCredit")
      );
      return couponLine?.label || null;
    },
    getTaxes(data) {
      let temp = 0;
      if (data)
        data.forEach((element) => (temp += element.tax_rate_override / 100));
      return temp;
    },
    getContent(invoice, content, str = null) {
      let temp = content;
      for (let i = 0; i < invoice.data.data.rules.length; i++) {
        if (
          str == "closed" &&
          invoice.data.data.rules[i].tax_rule.includes("Closed Liquids") &&
          content != null
        ) {
          temp = content.replace("{sum of ml}", invoice.data.data.rules[i].total);
          break;
        } else if (
          str == "open" &&
          invoice.data.data.rules[i].tax_rule.includes("Open Liquids") &&
          content != null
        ) {
          temp = content.replace("{sum of ml}", invoice.data.data.rules[i].total);
          break;
        } else if (str == "text" && content != null) {
          let total = invoice.data.data.rules.reduce((a, b) => a + b.total, 0);
          temp = content.replace("{sum of ml}", total);
          break;
        } else if (content != null) {
          temp = content.replace("{sum of ml}", invoice.data.data.rules[i].total);
        }
      }
      return temp != null && temp.includes("{sum of ml}") ? "" : temp;
    },
    packingList(invoice) {
      if (invoice.data.data.packingList) {
        this.$router.push("/packing-list/" + invoice.data.data.order_id);
      }
    },
    getSubTotal(invoice) {
      let temp = 0;
      if (this.adminInvoice) {
        invoice.data.data.invoice_line_items.forEach(
          (element) => (temp += element.quantity * this.getGGVPrice(element))
        );
      } else {
        invoice.data.data.invoice_line_items.forEach((element) => {
          if (element.line_type === "product") {
            temp += element.quantity * this.getPrice(element);
          }
        });
      }
      return temp;
    },
    getGGVPrice(item) {
      if (item.tags && !item.tags.includes("GGV ONLY")) {
        return "0.35";
      }
      return item.mac_price * 1.08;
    },
  },
  setup() {
    const { invoice, Invoice, loader, downloadInvoice } = Account();
    Invoice();

    const route = useRoute();
    const adminInvoice = ref(false);

    onMounted(() => {
      if (route.name == "Admin Invoice") {
        adminInvoice.value = true;
      }
    });
    return {
      loader,
      invoice,
      adminInvoice,
      downloadInvoice
    };
  },
};
</script>

<style>
@page {
  size: A4;
  margin: 15mm 0;
}

@page :first {
  margin-top: 0in;
}

@media print {
  html,
  body {
    @apply p-0 m-0 bg-white overflow-visible w-full;
    font-size: 11px;
    -webkit-print-color-adjust: exact;
    height: 282mm;
  }
  .hide-on-print {
    visibility: hidden;
    display: none;
  }
}
</style>

<style lang="scss" scoped>
.invoice {
  @apply w-full h-full p-8 bg-theme-bg bg-opacity-30;
  overflow-x: auto;

  @media print {
    @apply bg-white;
  }

  .inv-inner {
    @apply max-w-5xl mx-auto bg-white shadow-md;
    min-width: 800px;

    @media print {
      @apply max-w-full shadow-none;
      page-break-after: always;
    }
  }

  .inv-header {
    @apply w-full px-10 py-8 flex items-center justify-between;
    background-color: rgb(248, 248, 248);

    .inv-logo {
      @apply flex items-center space-x-1;

      span {
        @apply text-2xl leading-none font-bold text-theme-secondary uppercase;
      }
    }

    .inv-customer-name {
      @apply text-sm font-semibold uppercase tracking-wide text-center;
      color: #485a5d;
      padding-right: inherit;
    }

    .inv-number {
      @apply flex items-center text-xs font-medium space-x-3;
      color: rgba(72, 90, 93, 0.54);
      span {
        min-width: 75px;
      }

      p {
        @apply text-sm;
        color: #485a5d;
      }
    }
  }

  .inv-icon {
    @apply w-full flex items-center justify-center pb-10 relative;

    span {
      @apply flex items-center justify-center w-14 h-14 border  rounded-full flex-shrink-0 bg-white -mt-7;
      border-color: #dbdbdb;

      svg {
        @apply w-9 h-9;
      }
    }
  }
  .badges {
    @apply flex w-full justify-end px-10 -mt-4 pb-1;
    .invoice-status {
      @apply inline-block border rounded-md border-gray-800 px-4 py-1 leading-none;

      span {
        @apply text-sm font-medium leading-none uppercase;
      }
    }
    .unpaid {
      @apply border-red-500 bg-red-500 text-red-600 bg-opacity-5;
    }
    .paid {
      @apply border-green-500 bg-green-500 text-green-600 bg-opacity-5;
    }
    .partial {
      @apply border-yellow-500 bg-yellow-500 text-yellow-600 bg-opacity-5;
    }
  }

  .inv-details {
    @apply flex items-start justify-between text-sm text-theme-body px-10 pb-8 leading-normal;

    .address-grid {
      @apply grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6;

      @media print {
        @apply grid grid-cols-2 gap-x-12 gap-y-6;
      }
    }

    .small {
      @apply inline-block text-xs text-theme-body uppercase font-normal mb-1.5;
    }

    .address {
      @apply flex flex-col space-y-7;

      > div {
        @apply w-full;
      }
      p {
        @apply text-theme-secondary mb-1;
      }
    }

    .details {
      @apply pt-5;
      .detail-item {
        @apply flex items-center py-1;

        span {
          @apply w-40;
        }

        p {
          @apply text-theme-secondary text-left;
        }
      }
    }
  }

  .inv-list {
    @media print {
      @apply px-10;
      page-break-inside: auto;
    }
    .inv-list-header {
      @apply flex items-center w-full px-10 py-4 border-b text-theme-body font-normal uppercase text-sm;
      border-color: rgba(180, 180, 180, 0.25);

      @media print {
        @apply px-0;
      }
    }
    .name {
      @apply flex-grow;
    }

    .price,
    .qty,
    .tax {
      @apply w-24;
    }

    .total {
      @apply w-24 text-center;
    }

    .inv-list-items {
      @apply w-full px-10 divide-y divide-gray-100;

      @media print {
        @apply px-0;
        page-break-inside: auto;
      }

      .item-info {
        @apply flex flex-col space-y-1.5 leading-none text-md font-medium;

        p {
          @apply text-theme-secondary text-md;

          @media print {
            font-size: 1rem;
          }
        }

        span {
          @apply text-theme-body text-sm;
        }
      }

      .inv-list-item {
        @apply flex items-center py-4 text-md text-theme-body font-normal;

        @media print {
          @apply py-3;
          page-break-inside: avoid;
          page-break-after: auto;
        }

        .name {
          @apply flex items-center space-x-4 text-md;

          img {
            @apply w-12 h-12;
          }
        }
        .total {
          @apply font-medium text-theme-dark text-md text-center;
        }

        @media print {
          .name,
          .total,
          .qty,
          .price {
            font-size: 1rem;
            font-weight: 400;
          }
        }
      }
    }
  }

  .inv-pricing {
    @apply grid grid-cols-2 py-5 px-10 mb-12 border-t gap-32;
    border-color: rgba(180, 180, 180, 0.25);

    @media print {
      page-break-inside: avoid;
      page-break-after: auto;
    }

    .order-pricings {
      @apply flex flex-col items-end;
    }

    .taxt-pricings {
      @apply flex flex-col items-start;
    }

    @media print {
      @apply px-5;
    }

    .price-item {
      @apply inline-flex items-center justify-between flex-shrink-0 w-full text-md text-theme-dark-shade font-light space-y-1.5;

      p {
        @apply w-3/5;
      }

      span {
        @apply w-2/5 text-right;
      }

      @media print {
        p,
        span {
          font-size: 1rem;
        }

        span {
          @apply text-right;
        }
      }

      &.highlight {
        @apply text-theme-secondary font-medium py-2 border-t mt-2;
        border-color: rgba(180, 180, 180, 0.25);

        &.no-border {
          @apply border-none py-0 mt-0;
        }
      }

      &.extra {
        @apply font-medium text-sm;
        color: #8fa3a9;
      }
    }
  }

  .inv-footer {
    @apply w-full px-10 py-6 border-t  flex items-start justify-between text-sm font-light text-theme-body leading-normal;
    border-color: rgba(180, 180, 180, 0.25);

    span {
      @apply block;
    }
  }
}
</style>
